/**
 * Utility to lazily import Pinia action functions to avoid circular dependencies.
 *
 * When a Pinia store imports action functions from separate files, and those files
 * call `useXxxStore()`, a circular dependency is created. This utility defers the
 * module import until the action is called at runtime.
 *
 * @example
 * // Instead of:
 * import { deposit, withdraw } from './message'
 * actions: { deposit, withdraw }
 *
 * // Use:
 * actions: {
 *   deposit: lazyPiniaAction(() => import('./message'), 'deposit'),
 *   withdraw: lazyPiniaAction(() => import('./message'), 'withdraw')
 * }
 */
export function lazyPiniaAction<
  TModule extends Record<string, (...args: any[]) => any>,
  TKey extends keyof TModule
>(
  importFn: () => Promise<TModule>,
  actionName: TKey
): (
  ...args: Parameters<TModule[TKey]>
) => Promise<Awaited<ReturnType<TModule[TKey]>>> {
  return async (...args: Parameters<TModule[TKey]>) => {
    const module = await importFn()
    const action = module[actionName] as TModule[TKey]

    return action(...args)
  }
}
